import { cs } from '../../lib/currency';
/**
 * Parent-scoped invoice table panel (iter272).
 *
 * Drop-in replacement for the old "Pending Invoices" mini-table on the
 * Parent Detail page. Fetches the unified `/admin/invoices/all` ledger,
 * filters down to this parent, and renders the exact same <InvoiceTable />
 * the admin Invoice Management page uses — so every per-row action
 * (View / Edit / Send Reminder / Charge / Mark Paid Offline / Void /
 * Unvoid / Refund / Send Receipt) is available right from the parent
 * page.
 *
 * Bulk reconcile + manual-create live on the Invoice Management page only;
 * those are admin-wide tools, not parent-scoped, so we intentionally
 * leave them out here.
 *
 * Props:
 *   - parentId   (string) — the parent whose invoices to surface
 *   - parentEmail (string?) — used as a secondary match for lane bookings
 *                            that may key off `customer_id` rather than
 *                            `parent_id` on legacy rows.
 *   - defaultStatus ('overdue' | 'processing' | 'paid' | 'all') — initial
 *                            status filter. Defaults to 'overdue'.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { Receipt, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

import InvoiceTable from './invoice/InvoiceTable';
import { sortInvoices } from './invoice/sortInvoices';
import ChargeCardDialog from './invoice/ChargeCardDialog';
import MarkPaidDialog from './invoice/MarkPaidDialog';
import InvoiceViewDialog from './invoice/InvoiceViewDialog';
import EditInvoiceDialog from './invoice/EditInvoiceDialog';
import VoidInvoiceDialog from './invoice/VoidInvoiceDialog';
import ReminderDialog from './invoice/ReminderDialog';
import BulkChargeDialog from './invoice/BulkChargeDialog';
import RefundDialog from './RefundDialog';
import { invoiceKey, isReminderEligible } from './invoice/utils.jsx';

// iter307b — Must match the option set in InvoiceFilters.jsx so the
// parent-scoped invoices panel can surface refunded MIs (e.g. the
// iter306ai reversal batch). Pre-fix the panel was missing
// "Refunded" entirely, so reversed charges appeared invisible from
// the parent's billing tab.
const STATUS_OPTIONS = [
  { value: 'overdue', label: 'Overdue (all unpaid)' },
  { value: 'all', label: 'All' },
  { value: 'processing', label: 'Processing (ACH)' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
  { value: 'refunded', label: 'Refunded' },
];

export default function ParentInvoicesPanel({
  parentId,
  defaultStatus = 'overdue',
}) {
  const [statusFilter, setStatusFilter] = useState(defaultStatus);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sort + pagination — mirrors InvoiceManagement defaults.
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Per-row dialogs.
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [chargeDialog, setChargeDialog] = useState(false);
  const [markPaidDialog, setMarkPaidDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [voidDialog, setVoidDialog] = useState(false);
  const [refundDialog, setRefundDialog] = useState(false);

  // Bulk + single reminder share one dialog component.
  const [selectedIds, setSelectedIds] = useState([]);
  const [reminderDialog, setReminderDialog] = useState({ open: false, mode: 'bulk', single: null });
  const [bulkChargeOpen, setBulkChargeOpen] = useState(false);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      // Server-side customer scope: the API only builds THIS parent's
      // invoices (was the whole tenant's ledger filtered client-side).
      const res = await api.get('/admin/invoices/all', {
        params: { status: statusFilter, parent_id: parentId },
      });
      // The API authorizes and scopes this request to parentId. Do not
      // re-filter the normalized rows here: a historical tournament record
      // can be found by its player while its legacy parent fields are blank.
      setInvoices(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [parentId, statusFilter]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // Reset selection + pagination on status / sort change.
  useEffect(() => { setSelectedIds([]); setCurrentPage(1); }, [statusFilter]);
  useEffect(() => { setCurrentPage(1); }, [sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const filteredInvoices = useMemo(
    () => sortInvoices(invoices, sortColumn, sortDirection),
    [invoices, sortColumn, sortDirection],
  );

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / itemsPerPage));
  const paginatedInvoices = filteredInvoices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // ----- Selection (bulk reminder) -----
  const eligiblePageInvoices = paginatedInvoices.filter(isReminderEligible);
  const eligibleFilteredInvoices = filteredInvoices.filter(isReminderEligible);
  const allPageSelected = eligiblePageInvoices.length > 0
    && eligiblePageInvoices.every((inv) => selectedIds.includes(invoiceKey(inv)));

  const toggleRow = (inv) => {
    const key = invoiceKey(inv);
    setSelectedIds((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };
  const togglePage = () => {
    const pageKeys = eligiblePageInvoices.map(invoiceKey);
    setSelectedIds((prev) => (allPageSelected
      ? prev.filter((k) => !pageKeys.includes(k))
      : Array.from(new Set([...prev, ...pageKeys]))));
  };
  const selectAllMatchingFilter = () => setSelectedIds(eligibleFilteredInvoices.map(invoiceKey));
  const clearSelection = () => setSelectedIds([]);

  // Combined payment link is lane-booking-specific. We re-use the parent
  // page's existing "Pay Now" / consolidation flows there — keep the
  // button hidden inside this parent-scoped panel to avoid dialog sprawl.
  const bulkLaneEligible = { eligible: false, reason: 'Bulk payment link is on the Invoice Management page' };

  // ----- Row + bulk actions -----
  const openView = (invoice) => { setSelectedInvoice(invoice); setViewDialog(true); };
  const openEdit = (invoice) => { setSelectedInvoice(invoice); setEditDialog(true); };
  const openCharge = (invoice) => { setSelectedInvoice(invoice); setChargeDialog(true); };
  const openMarkPaid = (invoice) => { setSelectedInvoice(invoice); setMarkPaidDialog(true); };

  // iter369 — Force-settle a paid/refunded invoice from the parent profile
  // (same action as Invoice Management). Stops reminders + clears it from
  // the underpaid scan.
  const handleMarkSettled = async (invoice) => {
    const TYPE_MAP = {
      small_group: 'small_group_invoice', enrollment: 'enrollment',
      tournament: 'tournament_registration', lane_booking: 'lane_booking',
      manual: 'manual_invoice', uniform: 'manual_invoice',
    };
    const entityType = TYPE_MAP[invoice.type] || 'manual_invoice';
    const entityId = invoice.type === 'small_group'
      ? String(invoice.id).replace(/^sg-/, '')
      : invoice.id;
    if (!window.confirm(
      `Mark "${invoice.invoice_number || invoice.description || invoice.id}" fully paid and stop all payment reminders for it?`
    )) return;
    try {
      await api.post('/admin/invoices/mark-settled', {
        entity_type: entityType,
        entity_id: entityId,
        monthly_invoice_id: invoice.monthly_invoice_id || undefined,
      });
      toast.success('Invoice marked fully paid — reminders stopped');
      fetchInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark as settled');
    }
  };
  const openVoid = (invoice) => { setSelectedInvoice(invoice); setVoidDialog(true); };
  const openRefund = (invoice) => { setSelectedInvoice(invoice); setRefundDialog(true); };

  const handleUnvoid = async (invoice) => {
    if (!window.confirm(
      `Restore "${invoice.invoice_number || invoice.id}" back to pending?\n\n`
      + 'The void reason will be cleared but kept on the audit trail (last_void_reason).',
    )) return;
    try {
      // iter306p: the list returns `type`; falling back to invoice_type
      // and finally 'manual'. Previously this defaulted every non-manual
      // invoice to 'manual' which produced "Manual invoice not found".
      const inv_type = invoice.type || invoice.invoice_type || 'manual';
      const idForApi = inv_type === 'small_group'
        ? String(invoice.id).replace(/^sg-/, '')
        : invoice.id;
      await api.post('/admin/invoices/unvoid', { invoice_id: idForApi, invoice_type: inv_type });
      toast.success('Invoice restored — back to pending');
      fetchInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Unvoid failed');
    }
  };

  const handleSendReceipt = async (invoice) => {
    const target = invoice.parent_email || invoice.email || invoice.parent_name || 'the parent';
    if (!window.confirm(
      `Send a paid-invoice receipt email for "${invoice.description || invoice.type}" `
      + `(${cs()}${(invoice.amount || 0).toFixed(2)}) to ${target}?`,
    )) return;
    try {
      const idForApi = invoice.type === 'small_group'
        ? String(invoice.id).replace(/^sg-/, '')
        : invoice.id;
      const { data } = await api.post('/admin/invoices/send-receipt', {
        invoice_type: invoice.type,
        invoice_id: idForApi,
      });
      toast.success(
        `Receipt emailed to ${data.sent_to} — ${cs()}${(data.amount || 0).toFixed(2)} `
        + `(${data.payment_method || 'card'})`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send receipt');
    }
  };

  const openSingleReminderDialog = (invoice) => {
    setReminderDialog({ open: true, mode: 'single', single: invoice });
  };
  const openBulkReminderDialog = () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one invoice');
      return;
    }
    setReminderDialog({ open: true, mode: 'bulk', single: null });
  };
  const openBulkChargeDialog = () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one invoice');
      return;
    }
    setBulkChargeOpen(true);
  };
  const selectedRows = useMemo(
    () => filteredInvoices.filter((inv) => selectedIds.includes(invoiceKey(inv))),
    [filteredInvoices, selectedIds],
  );
  const handleRetainFailures = (failures) => {
    const failedKeys = new Set(failures.map((f) => `${f.type}:${f.id}`));
    setSelectedIds((prev) => prev.filter((k) => failedKeys.has(k)));
  };

  // ----- Refund dialog shape (mirrors InvoiceManagement) -----
  const refundContext = useMemo(() => {
    if (!selectedInvoice) return null;
    const TYPE_MAP = {
      small_group: 'small_group_invoice',
      enrollment: 'enrollment',
      tournament: 'tournament_registration',
      lane_booking: 'lane_booking',
      manual: 'manual_invoice',
    };
    const entityType = TYPE_MAP[selectedInvoice.type];
    const entityId = selectedInvoice.type === 'small_group'
      ? String(selectedInvoice.id).replace(/^sg-/, '')
      : selectedInvoice.id;
    return {
      entityType,
      entity: {
        id: entityId,
        // iter310 — Refund cap must be the actually-charged amount
        // (amount_charged) when it exists and differs from the invoice
        // face amount. Otherwise the "Max refundable" number reads as
        // the invoice's face value ($99) and admins can't refund the
        // full $165 that was actually debited on an over-charged /
        // legacy cycle. Falls back to invoice.amount for the vast
        // majority of rows where charged == invoice amount.
        price: Number(selectedInvoice.amount_charged) > 0
          ? Number(selectedInvoice.amount_charged)
          : selectedInvoice.amount,
        refunded_amount: selectedInvoice.refunded_amount || 0,
        customer_name: selectedInvoice.parent_name,
        payment_method: selectedInvoice.payment_method,
        // iter307ds — pass the underlying monthly_invoice id so the
        // backend refund flow can pick the correct cycle's Stripe PI
        // instead of an unrelated older payment_transactions row.
        monthly_invoice_id: selectedInvoice.monthly_invoice_id || undefined,
      },
      summaryLines: [
        { label: 'Customer', value: selectedInvoice.parent_name || '—' },
        { label: 'Description', value: selectedInvoice.description || '—' },
        { label: 'Type', value: selectedInvoice.type_label || selectedInvoice.type },
      ],
    };
  }, [selectedInvoice]);

  return (
    <Card data-testid="parent-invoices-panel">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Invoices
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger
                className="w-[140px] h-9"
                data-testid="parent-invoices-status-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && invoices.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading invoices…
          </div>
        ) : (
          <InvoiceTable
            paginatedInvoices={paginatedInvoices}
            filteredInvoices={filteredInvoices}
            loading={loading}
            onSort={handleSort}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            selectedIds={selectedIds}
            eligiblePageInvoices={eligiblePageInvoices}
            eligibleFilteredInvoices={eligibleFilteredInvoices}
            allPageSelected={allPageSelected}
            onTogglePage={togglePage}
            onToggleRow={toggleRow}
            onSelectAllMatching={selectAllMatchingFilter}
            onClearSelection={clearSelection}
            bulkLaneEligible={bulkLaneEligible}
            onOpenBulkPaymentLink={() => toast.info(
              'Combined payment link lives on the Invoice Management page.',
            )}
            onOpenBulkReminder={openBulkReminderDialog}
            onOpenBulkCharge={openBulkChargeDialog}
            onView={openView}
            onEdit={openEdit}
            onSendReminder={openSingleReminderDialog}
            onCharge={openCharge}
            onMarkPaid={openMarkPaid}
            onVoid={openVoid}
            onUnvoid={handleUnvoid}
            onRefund={openRefund}
            onSendReceipt={handleSendReceipt}
            onSyncUpdated={fetchInvoices}
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
            onPrevPage={() => setCurrentPage((p) => Math.max(1, p - 1))}
            onNextPage={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          />
        )}
      </CardContent>

      {/* Per-row dialogs — exact mirror of the InvoiceManagement page. */}
      <ChargeCardDialog
        open={chargeDialog}
        onOpenChange={setChargeDialog}
        invoice={selectedInvoice}
        onCharged={fetchInvoices}
      />
      <MarkPaidDialog
        open={markPaidDialog}
        onOpenChange={setMarkPaidDialog}
        invoice={selectedInvoice}
        onMarked={fetchInvoices}
      />
      <InvoiceViewDialog
        open={viewDialog}
        onOpenChange={setViewDialog}
        invoice={selectedInvoice}
        onCharge={openCharge}
        onMarkPaid={openMarkPaid}
        onMarkSettled={handleMarkSettled}
      />
      <EditInvoiceDialog
        open={editDialog}
        onOpenChange={setEditDialog}
        invoice={selectedInvoice}
        onEdited={fetchInvoices}
      />
      <VoidInvoiceDialog
        open={voidDialog}
        onOpenChange={setVoidDialog}
        invoice={selectedInvoice}
        onVoided={fetchInvoices}
      />
      <ReminderDialog
        open={reminderDialog.open}
        onOpenChange={(open) => setReminderDialog((d) => ({ ...d, open }))}
        mode={reminderDialog.mode}
        single={reminderDialog.single}
        bulkInvoices={selectedRows}
        onSent={() => { if (reminderDialog.mode === 'bulk') clearSelection(); }}
        onRetainFailures={handleRetainFailures}
      />
      <BulkChargeDialog
        open={bulkChargeOpen}
        onOpenChange={setBulkChargeOpen}
        parentId={parentId}
        invoices={selectedRows}
        onDone={() => { clearSelection(); fetchInvoices(); }}
      />
      {refundContext && (
        <RefundDialog
          open={refundDialog}
          onOpenChange={setRefundDialog}
          entityType={refundContext.entityType}
          entity={refundContext.entity}
          priceField="price"
          summaryLines={refundContext.summaryLines}
          allowCancelCheckbox={false}
          onRefunded={() => { setRefundDialog(false); fetchInvoices(); }}
        />
      )}
    </Card>
  );
}
